import { useMemo } from 'react';
import type { FlowEmail, FlowSummary, FlowTrigger } from '@/lib/api/email-flows/types';
import type { DelayUnit, EmailFormData, TemplateVariables } from '../types/email.types';
import { filterVariablesForTrigger, getTemplateType } from '../logic';
import { useEmailTemplateQuery } from '../queries/useEmailQueries';

interface UseEmailTemplateProps {
    flowData: FlowSummary | undefined;
    emailData: FlowEmail | undefined;
    emailId?: number;
}

interface UseEmailTemplateReturn {
    templateVariables: TemplateVariables;
    templateDefaults: EmailFormData;
    defaultValues: EmailFormData;
}

export function useEmailTemplate({ flowData, emailData, emailId }: UseEmailTemplateProps): UseEmailTemplateReturn {
    const emailCount = flowData?.flow_emails?.length || 0;
    const isFirstEmail = emailId ? emailCount === 1 : emailCount === 0;

    // Get template type
    const templateType = flowData ? getTemplateType(flowData, emailCount, isFirstEmail) : 'checkout_first';

    // Fetch template variables using query hook
    const { data: templateResponse } = useEmailTemplateQuery({
        templateType,
        enabled: !!flowData,
    });

    const template = templateResponse?.flow_email_template;
    const trigger = (flowData?.trigger || flowData?.trigger_type || 'checkout') as FlowTrigger;

    // Derive template variables and defaults from query data
    const templateVariables: TemplateVariables = useMemo(() => {
        if (!template) {
            return { subject: [], body: [] };
        }
        return filterVariablesForTrigger(template.variables, trigger);
    }, [template, trigger]);

    const templateDefaults: EmailFormData = useMemo(() => {
        if (!template) {
            return {
                subject: '',
                body: '',
                delay: '1',
                delayUnit: 'hours' as DelayUnit,
            };
        }
        return {
            subject: template.default.subject,
            body: template.default.body,
            delay: '1',
            delayUnit: 'hours' as DelayUnit,
        };
    }, [template]);

    // Derive defaultValues from emailData or templateDefaults
    const defaultValues: EmailFormData = useMemo(() => {
        if (emailId && emailData) {
            const emailDelay = emailData.data?.delay || emailData.delay || 0;

            // Convert delay from seconds to value + unit
            const generateDelayFromSeconds = (seconds: number): { value: number; unit: DelayUnit } => {
                if (seconds % 86400 === 0) {
                    return { value: seconds / 86400, unit: 'days' };
                }
                if (seconds % 3600 === 0) {
                    return { value: seconds / 3600, unit: 'hours' };
                }
                return { value: seconds / 60, unit: 'minutes' };
            };

            const { value, unit } = generateDelayFromSeconds(emailDelay);
            return {
                subject: emailData.data?.subject || emailData.subject || '',
                body: emailData.data?.body || emailData.content || '',
                delay: value.toString(),
                delayUnit: unit,
            };
        }

        // For new emails (no emailId), use template defaults but override delayUnit for cart abandonment
        const isCartAbandonment = trigger === 'cart-abandonment';
        return {
            ...templateDefaults,
            delayUnit: isCartAbandonment ? 'days' : templateDefaults.delayUnit,
        };
    }, [emailId, emailData, templateDefaults, trigger]);

    return {
        templateVariables,
        templateDefaults,
        defaultValues,
    };
}
